import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import DisableConfirm from "@/components/DisableConfirm";
import { toast } from "sonner";
import { Eye, Pencil, Power, Plus, RotateCcw, Search } from "lucide-react";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}

export default function OrganizationsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [viewItem, setViewItem] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/organizations", { params: { search: search || undefined } });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const handleDisable = async () => {
    if (!disableTarget) return;
    setDisabling(true);
    try {
      await api.patch(`/organizations/${disableTarget.id}/disable`);
      toast.success("Organization disabled");
      setDisableTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDisabling(false); }
  };

  const handleEnable = async (item) => {
    try {
      await api.patch(`/organizations/${item.id}/enable`);
      toast.success("Organization re-enabled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="organizations-list-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 01</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
            Organizations
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Indian-standard registry. Soft-delete keeps every record auditable. Sorted by creation date (newest first).
          </p>
        </div>
        <Button asChild data-testid="org-create-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
          <Link to="/organizations/new"><Plus size={16} className="mr-1.5" />Create Organization</Link>
        </Button>
      </div>

      <form onSubmit={onSearch} className="mb-4 flex max-w-md items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="org-search-input"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-sm pl-9"
          />
        </div>
        <Button type="submit" variant="outline" data-testid="org-search-btn" className="rounded-sm">Search</Button>
      </form>

      <div className="rounded-sm border border-slate-200 bg-white">
        <Table data-testid="org-table" className="data-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>PAN</TableHead>
              <TableHead>Incorporated</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items === null && [...Array(5)].map((_, i) => (
              <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            ))}
            {items && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500">No organizations found.</TableCell></TableRow>
            )}
            {items?.map((it) => (
              <TableRow key={it.id} data-testid={`org-row-${it.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {it.logo_base64 ? (
                      <img src={it.logo_base64} alt="" className="h-8 w-8 rounded-sm border border-slate-200 object-cover" />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-slate-100 text-xs font-semibold text-slate-500">
                        {(it.name || "?").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="font-medium text-slate-900">{it.name}</div>
                      {it.is_80g_enabled && (it.type === "NGO" || it.type === "Trust") && (
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">80G compliant</div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell><span className="rounded-sm border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-700">{it.type}</span></TableCell>
                <TableCell className="font-mono text-xs">{it.gstin || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{it.pan_number || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{fmtDate(it.incorporation_date)}</TableCell>
                <TableCell><StatusBadge status={it.status} /></TableCell>
                <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 160 }}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" title="View details" aria-label="View" onClick={() => setViewItem(it)} data-testid={`org-view-${it.id}`} className="h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900"><Eye size={16} /></Button>
                    <Button size="icon" variant="ghost" title="Edit organization" aria-label="Edit" onClick={() => navigate(`/organizations/${it.id}/edit`)} data-testid={`org-edit-${it.id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-indigo-50 hover:text-[#00227A]"><Pencil size={16} /></Button>
                    {it.status !== "disabled" ? (
                      <Button size="icon" variant="ghost" title="Disable (soft delete)" aria-label="Disable" onClick={() => setDisableTarget(it)} data-testid={`org-disable-${it.id}`} className="h-8 w-8 rounded-sm text-rose-600 hover:bg-rose-50 hover:text-rose-700"><Power size={16} /></Button>
                    ) : (
                      <Button size="icon" variant="ghost" title="Re-enable" aria-label="Re-enable" onClick={() => handleEnable(it)} data-testid={`org-enable-${it.id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"><RotateCcw size={16} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!viewItem} onOpenChange={(o) => !o && setViewItem(null)}>
        <DialogContent data-testid="org-view-modal" className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle>{viewItem?.name}</DialogTitle>
            <DialogDescription>Full organization record</DialogDescription>
          </DialogHeader>
          {viewItem && (
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Info label="Type" value={viewItem.type} />
              <Info label="Status" value={<StatusBadge status={viewItem.status} />} />
              <Info label="Registration No (CIN)" value={viewItem.registration_number} mono />
              <Info label="PAN" value={viewItem.pan_number} mono />
              <Info label="GSTIN" value={viewItem.gstin} mono />
              <Info label="Incorporation Date" value={fmtDate(viewItem.incorporation_date)} />
              <Info label="80G Enabled" value={viewItem.is_80g_enabled ? "Yes" : "No"} />
              <Info label="Address" value={viewItem.address} />
              <Info label="Created" value={fmtDate(viewItem.created_at)} />
              <Info label="Created by IP" value={viewItem.created_by_ip} mono />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DisableConfirm
        open={!!disableTarget}
        onOpenChange={(o) => !o && setDisableTarget(null)}
        onConfirm={handleDisable}
        loading={disabling}
        entityLabel="organization"
        warningMessage="Disabling this organization will block creation of new publication houses under it. Active publication houses must be disabled first. This is a soft-delete; the record is preserved for compliance."
      />
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div className="rounded-sm border border-slate-200 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
